import AppDateRangePicker from '@nxsys/components/AppDateRangePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { EXPORT_PERIOD_MODE } from '@nxsys/store/slices/export/types';
import { FILE_TYPE_VALUE } from '@nxsys/utils';
import { appConfigManager } from '@nxsys/utils/appConfigs';
import { getRangeYear, TAX_PERIOD_OPTIONS } from '@nxsys/utils/taxYear';
import { Alert, Form, FormProps, Radio, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { orderBy } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

// A UK tax year starts on 6 April, or on the earlier day the tenant ran its
// year end (dateChangeToNewTaxYear): pay dates from then on belong to the new
// tax year. The backend reads that day in UTC. Without one, 6 April.
const ukTaxYearStart = (taxYear: number, yearEndRanOn?: string): Dayjs => {
  const sixthOfApril = dayjs(`${taxYear}-04-06`);
  if (!yearEndRanOn) return sixthOfApril;
  const ranOnDay = dayjs(new Date(yearEndRanOn).toISOString().slice(0, 10));
  return ranOnDay.year() === taxYear && ranOnDay.isBefore(sixthOfApril, 'day')
    ? ranOnDay
    : sixthOfApril;
};

// A UK tax year runs to the day before the next one starts, an Irish one is
// a calendar year.
const taxYearDates = (
  taxYear: number,
  isIreland: boolean,
  yearEndRanOn?: string,
): [Dayjs, Dayjs] =>
  isIreland
    ? [dayjs(`${taxYear}-01-01`), dayjs(`${taxYear}-12-31`)]
    : [
        ukTaxYearStart(taxYear, yearEndRanOn),
        ukTaxYearStart(taxYear + 1, yearEndRanOn).subtract(1, 'day'),
      ];

type ExportFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onExport: (values: any) => void;
};

const ExportFilterModal: React.FC<ExportFilterModalProps> = ({
  isOpen,
  toggle,
  onExport,
}: ExportFilterModalProps) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');

  const [form] = Form.useForm();
  const watchFileType = Form.useWatch('fileType', form);
  const watchPeriodMode = Form.useWatch('periodMode', form);
  const watchTaxYear = Form.useWatch('taxYear', form);

  // The pay dates that can be picked: the days of the tax year chosen. The
  // calendar opens on today, or on the nearest end of that tax year.
  const [taxYearStart, taxYearEnd] = taxYearDates(
    Number(watchTaxYear),
    isIreland,
    appConfigManager.getTaxCalendar()?.dateChangeToNewTaxYear,
  );
  const today = dayjs();
  const calendarOpensOn = today.isBefore(taxYearStart)
    ? taxYearStart
    : today.isAfter(taxYearEnd)
      ? taxYearEnd
      : today;

  const fileTypeOptions = [
    ...(isBureau
      ? [
          {
            label: messages['export.company.all'],
            value: FILE_TYPE_VALUE.ALL,
          },
        ]
      : [
          {
            label: messages['common.agency'],
            value: FILE_TYPE_VALUE.AGENCY,
          },
        ]),
    {
      label: messages['export.company.employee'],
      value: FILE_TYPE_VALUE.SUBCONTRACTOR,
    },
    {
      label: messages['export.company.payroll'],
      value: FILE_TYPE_VALUE.PAYROLL,
    },
    {
      label: messages['export.company.payslip'],
      value: FILE_TYPE_VALUE.SELF_BILL,
    },
  ];

  const FORM_ITEMS = [
    {
      label: messages['export.company.fileType'],
      name: 'fileType',
      type: 'select',
      options: fileTypeOptions,
      placeholder: messages['export.company.selectFileType'],
      required: true,
    },
    ...(watchFileType &&
    watchFileType !== FILE_TYPE_VALUE.SUBCONTRACTOR &&
    watchFileType !== FILE_TYPE_VALUE.AGENCY &&
    watchFileType !== FILE_TYPE_VALUE.ALL
      ? [
          // First, as the pay dates of a custom range are limited to it.
          {
            label: messages['common.taxYear'],
            name: 'taxYear',
            type: 'select',
            options: taxYearRange.map((option) => ({
              label: option.label,
              value: option.value.toString(),
            })),
            required: true,
          },
          {
            label: 'Export Period',
            name: 'periodMode',
            type: 'radio',
            options: [
              // One period or several, from the same picker.
              { label: 'Specific Periods', value: EXPORT_PERIOD_MODE.SPECIFIC },
              { label: 'Custom Range', value: EXPORT_PERIOD_MODE.CUSTOM_RANGE },
              { label: 'All Tax Periods', value: EXPORT_PERIOD_MODE.ALL },
            ],
            required: false,
          },
          ...(watchPeriodMode === EXPORT_PERIOD_MODE.SPECIFIC
            ? [
                {
                  label: messages['table.taxPeriod'],
                  name: 'taxPeriods',
                  type: 'selectTaxWeekMonthMultiple',
                  placeholder: 'Select one or more tax periods',
                  required: true,
                },
              ]
            : []),
          ...(watchPeriodMode === EXPORT_PERIOD_MODE.CUSTOM_RANGE
            ? [
                {
                  label: messages['table.payDate'],
                  name: 'payDates',
                  type: 'payDateRange',
                  required: true,
                },
              ]
            : []),
        ]
      : []),
  ];

  const onFinish: FormProps['onFinish'] = (values) => {
    onExport(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['export.company.selectOptions']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.export'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      minHeight="0px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        // Pay dates belong to the tax year: picking another year clears them.
        onValuesChange={(changed: Record<string, unknown>) => {
          if ('taxYear' in changed)
            form.setFieldsValue({ payDates: undefined });
        }}
        initialValues={{
          periodMode: EXPORT_PERIOD_MODE.SPECIFIC,
          taxYear: taxYearRange[0]?.value?.toString(),
        }}
      >
        {FORM_ITEMS.map((formItem) => (
          <Form.Item
            key={formItem.name}
            label={formItem.label}
            name={formItem.name}
            rules={[
              {
                required: formItem.required,
              },
            ]}
          >
            {formItem.type === 'select' && (
              <AppSelect
                placeholder={formItem.placeholder}
                options={formItem.options ?? []}
                onChange={(value) => {
                  form.setFieldsValue({
                    [formItem.name]: value.value,
                  });
                }}
              />
            )}
            {formItem.type === 'payDateRange' && (
              <AppDateRangePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder={[
                  messages['common.fromDate'],
                  messages['common.toDate'],
                ]}
                disabledDate={(date: Dayjs) =>
                  date.isBefore(taxYearStart, 'day') ||
                  date.isAfter(taxYearEnd, 'day')
                }
                defaultPickerValue={[calendarOpensOn, calendarOpensOn]}
              />
            )}
            {formItem.type === 'selectTaxWeekMonthMultiple' && (
              <Select
                mode="multiple"
                allowClear
                placeholder={formItem.placeholder}
                options={TAX_PERIOD_OPTIONS}
                optionFilterProp="label"
              />
            )}
            {formItem.type === 'radio' && (
              <Radio.Group options={formItem.options ?? []} />
            )}
          </Form.Item>
        ))}
        {(watchFileType === FILE_TYPE_VALUE.ALL ||
          watchPeriodMode === EXPORT_PERIOD_MODE.ALL) && (
          <Alert
            type="warning"
            showIcon
            className="mt-2"
            message="Exporting all data can take a long time to complete."
          />
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default ExportFilterModal;
